/**
 * Раздел «Настройки» (тикет T18b, спецификация § 14).
 *
 * Всё, что здесь происходит, делает ядро клиента: правка видна сразу, отказ сервера
 * возвращает прежнее и объясняется словами. Экран лишь рисует управление, подходящее
 * значению, и следит за тем, чтобы у каждого была настоящая подпись.
 *
 * Чужих Настроек здесь нет и быть не может (§ 14, критерий 13): дверь к серверу говорит
 * «мои», и другого адреса у неё не существует — ни для Администратора, ни для кого-либо
 * ещё. Об этом сказано прямо на экране, чтобы человек не гадал, видит ли начальник, какую
 * он выбрал тему.
 *
 * Разделы стоят слева, пункты выбранного раздела — рядом (правка 2026-08-31). До этого все
 * семь разделов лежали одной лентой сверху вниз: чтобы дойти от «Вида» до «Присутствия»,
 * человек прокручивал четыре экрана мимо всего, что ему сейчас не нужно, и «где включаются
 * уведомления» становилось поиском, а не выбором. Теперь на виду ровно один раздел, а
 * остальные названы рядом — видно и куда попал, и что вообще бывает.
 *
 * Разделы сделаны вкладками (`tablist`), а не просто кнопками: диктор объявляет «вкладка
 * Уведомления, четвёртая из семи», стрелки ходят по разделам, а Tab уводит сразу внутрь
 * открытого — иначе с клавиатуры пришлось бы протыкать все семь названий, чтобы добраться
 * до первого переключателя.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Button, Choice, Notice, Switch } from '../ui/controls.js';
import { useSettings } from '../state/settings.js';
import { choiceLabel, SETTING_FORM, settingLabel } from '../i18n/settings-words.js';
import { clockToMinutes, minutesToClock } from '../settings/minutes.js';
import type { PhraseKey } from '../i18n/dictionary.js';

const GROUP_PHRASE: Readonly<Record<string, PhraseKey>> = {
  appearance: 'settings.group.appearance',
  language: 'settings.group.language',
  accessibility: 'settings.group.accessibility',
  notifications: 'settings.group.notifications',
  sound: 'settings.group.sound',
  messaging: 'settings.group.messaging',
  presence: 'settings.group.presence',
};

/**
 * Часовые пояса спрашиваем у самого устройства счёта времени: список поясов меняют
 * правительства, и переписанный руками он однажды отстанет. Старый браузер такого списка
 * не отдаёт — тогда показываем то, что уже выбрано, и не мешаем человеку работать.
 */
const timeZones = (current: string): readonly string[] => {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  const all = typeof supported === 'function' ? supported('timeZone') : [];
  return all.length > 0 ? all : [current];
};

export function Settings({ onBack }: { readonly onBack: () => void }): ReactNode {
  const { editor, state, t, language } = useSettings();
  const [clocks, setClocks] = useState<Readonly<Record<string, string>>>({});
  /** Какой раздел открыт. Пустая строка — «ещё не выбирали», тогда берём первый. */
  const [openGroup, setOpenGroup] = useState('');
  const tabsRef = useRef<HTMLDivElement>(null);
  /**
   * Стоят ли разделы столбиком. На узком экране они ложатся строкой, и диктору надо
   * говорить правду: объяви мы «столбик» там, где глазами видна строка, человек с остатком
   * зрения искал бы стрелками не в ту сторону. Ходят по разделам всё равно обе пары стрелок.
   */
  const [column, setColumn] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 48rem)').matches,
  );
  useEffect(() => {
    const watch = window.matchMedia('(min-width: 48rem)');
    const react = (): void => setColumn(watch.matches);
    watch.addEventListener('change', react);
    return () => watch.removeEventListener('change', react);
  }, []);

  // Набранное в поле времени держим отдельно: пока человек печатает «2», это ещё не
  // «02:00», и отправлять такое на сервер рано.
  useEffect(() => {
    setClocks({
      quietFrom: minutesToClock(state.settings['quietFrom']),
      quietTo: minutesToClock(state.settings['quietTo']),
    });
  }, [state.settings]);

  const groups = useMemo(() => Object.entries(state.groups), [state.groups]);

  // Раздел мог исчезнуть — сервер вправе прислать другой набор, — и тогда запомненное имя
  // указывает в пустоту. Падаем на первый: экран без единого пункта человек прочитал бы
  // как поломку, а не как «этого раздела больше нет».
  const current = groups.some(([group]) => group === openGroup) ? openGroup : (groups[0]?.[0] ?? '');
  const keys = groups.find(([group]) => group === current)?.[1] ?? [];
  const groupTitle = (group: string): string =>
    GROUP_PHRASE[group] ? t(GROUP_PHRASE[group]) : group;

  // Ходить по разделам стрелками — обычай вкладок. Фокус переносим сами: у невыбранной
  // вкладки `tabIndex` равен −1, и без этого стрелка никуда бы не привела.
  const focusGroup = (index: number): void => {
    // Разделов может не быть вовсе — пока идёт загрузка. Остаток от нуля даёт NaN, и дальше
    // считать нечего.
    if (groups.length === 0) return;
    const safe = ((index % groups.length) + groups.length) % groups.length;
    const group = groups[safe]?.[0];
    if (group === undefined) return;
    setOpenGroup(group);
    tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[safe]?.focus();
  };

  const onTabsKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    const at = groups.findIndex(([group]) => group === current);
    switch (event.key) {
      // Вкладки стоят столбиком, но стрелки влево-вправо тоже слушаем: на иврите столбик
      // тот же, а рука тянется к той стрелке, куда смотрит текст.
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusGroup(at + 1);
        return;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusGroup(at - 1);
        return;
      case 'Home':
        event.preventDefault();
        focusGroup(0);
        return;
      case 'End':
        event.preventDefault();
        focusGroup(groups.length - 1);
        return;
      default:
    }
  };

  if (!editor) {
    return (
      <main id="main" className="p-6">
        <Notice kind="error">{t('settings.failed')}</Notice>
      </main>
    );
  }

  const change = (key: string, value: string | number | boolean): void => {
    void editor.change(key, value);
  };

  const control = (key: string): ReactNode => {
    const form = SETTING_FORM[key];
    const value = state.settings[key];
    const label = settingLabel(key, language);
    const busy = state.saving || state.loading;

    if (!form) {
      // Настройка из более новой версии сервера. Показываем как есть — прятать её значило
      // бы оставить человека без управления тем, что у него уже работает.
      return (
        <p key={key} className="text-sm text-ink-soft">
          {label}: {String(value)}
        </p>
      );
    }

    switch (form.kind) {
      case 'switch':
        return (
          <Switch
            key={key}
            label={label}
            checked={value === true}
            disabled={busy}
            onToggle={(next) => change(key, next)}
          />
        );

      case 'choice':
        return (
          <Choice
            key={key}
            label={label}
            value={String(value ?? '')}
            disabled={busy}
            onChange={(event) => {
              const chosen = event.currentTarget.value;
              // Начало недели хранится числом, а показывается днём — иначе его пришлось бы
              // переводить, а порядок дней от языка не зависит.
              change(key, key === 'weekStart' ? Number(chosen) : chosen);
            }}
          >
            {form.choices.map((one) => (
              <option key={one} value={one}>
                {choiceLabel(key, one, language)}
              </option>
            ))}
          </Choice>
        );

      case 'zone':
        return (
          <Choice
            key={key}
            label={label}
            value={String(value ?? '')}
            disabled={busy}
            onChange={(event) => change(key, event.currentTarget.value)}
          >
            {timeZones(String(value ?? 'UTC')).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Choice>
        );

      case 'minutes':
        return (
          <div key={key} className="flex flex-col gap-1">
            {/* Подпись связана с полем по имени, а не вложением: часы и ползунок диктор
                читает особым образом, и вложенную подпись у них порой пропускает. */}
            <label htmlFor={`ktm-${key}`} className="text-sm text-ink-soft">
              {label}
            </label>
            <input
              id={`ktm-${key}`}
              type="time"
              value={clocks[key] ?? minutesToClock(value)}
              disabled={busy}
              onChange={(event) => {
                const typed = event.currentTarget.value;
                setClocks((was) => ({ ...was, [key]: typed }));
                const minutes = clockToMinutes(typed);
                // Недобранное время не отправляем — см. `clockToMinutes`.
                if (minutes !== null) change(key, minutes);
              }}
              className="rounded-md border border-line bg-surface px-3 py-2 text-ink"
            />
          </div>
        );

      case 'volume':
        return (
          <div key={key} className="flex flex-col gap-1">
            <label htmlFor={`ktm-${key}`} className="text-sm text-ink-soft">
              {label}: {String(value ?? 0)}
            </label>
            <input
              id={`ktm-${key}`}
              type="range"
              min={form.least}
              max={form.most}
              value={Number(value ?? 0)}
              disabled={busy}
              onChange={(event) => change(key, Number(event.currentTarget.value))}
              className="accent-[var(--ktm-accent)]"
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-dvh">
      <a href="#main" className="ktm-skip">
        {t('skip.toMain')}
      </a>

      <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2">
        <Button onClick={onBack}>{t('common.back')}</Button>
        <h1 className="text-lg font-semibold">{t('settings.title')}</h1>
        <span aria-live="polite" className="ms-auto text-sm text-ink-soft">
          {state.saving ? t('common.saving') : ''}
        </span>
      </header>

      <main id="main" className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
        <Notice kind="info">{t('settings.mineOnly')}</Notice>

        {state.refusal ? (
          <Notice kind="error" onClose={() => editor.dismissRefusal()} closeLabel={t('common.close')}>
            {state.refusal}
          </Notice>
        ) : null}

        {state.loading ? <p className="text-ink-soft">{t('common.loading')}</p> : null}

        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          {/* Разделы. На узком экране они ложатся строкой над содержимым: столбик в четверть
              ширины телефона обрезал бы названия до «Уведо…». */}
          <div
            ref={tabsRef}
            role="tablist"
            aria-orientation={column ? 'vertical' : 'horizontal'}
            aria-label={t('settings.groups')}
            onKeyDown={onTabsKey}
            className="flex shrink-0 flex-row flex-wrap gap-1 md:w-56 md:flex-col md:flex-nowrap"
          >
            {groups.map(([group]) => {
              const open = group === current;
              return (
                <button
                  key={group}
                  type="button"
                  role="tab"
                  id={`ktm-tab-${group}`}
                  aria-selected={open}
                  aria-controls={`ktm-panel-${group}`}
                  // Только у открытой вкладки Tab находит вход: остальные берут стрелками.
                  tabIndex={open ? 0 : -1}
                  onClick={() => setOpenGroup(group)}
                  className={`rounded-md border px-3 py-2 text-start text-sm transition-colors ${
                    open
                      ? 'border-line bg-surface-2 font-semibold text-ink'
                      : 'border-transparent text-ink-soft hover:bg-surface-2 hover:text-ink'
                  }`}
                >
                  {groupTitle(group)}
                </button>
              );
            })}
          </div>

          {/* Пункты открытого раздела. Заголовок здесь тоже есть: на телефоне вкладки
              уезжают вверх, и без него не видно, чей это список. */}
          {current ? (
            <section
              role="tabpanel"
              id={`ktm-panel-${current}`}
              aria-labelledby={`ktm-tab-${current}`}
              // Фокус панель берёт, только когда внутри брать нечего: иначе Tab с раздела
              // упирался бы в пустую рамку вокруг всей панели и лишь следующим нажатием
              // доходил до первого переключателя. Пустой раздел без этого промолчал бы вовсе.
              tabIndex={keys.length === 0 ? 0 : -1}
              className="flex min-w-0 flex-1 flex-col gap-3 rounded-md border border-line bg-surface p-4"
            >
              <h2 className="text-base font-semibold">{groupTitle(current)}</h2>
              {keys.map((key) => control(key))}
            </section>
          ) : null}
        </div>

        {state.ownKeys.length > 0 ? (
          <div>
            <Button look="plain" disabled={state.saving} onClick={() => void editor.reset()}>
              {t('settings.reset')}
            </Button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
